import { Word } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as execution from './execution'
import type { Item } from './item'

/**
 * Represents an activity in the system. Processes are essentially composed of
 * these activities
 *
 * However, there is a certain amount of indirection. These activities are meant
 * to represent actual things that happen in the system. Processes are meant to
 * represent a functionally meaningful progression for an end user. Processes
 * can typically derive a functionally meaningful progression by considering the
 * actual activity in the system, but they are not the same thing
 */
export interface Activity {
  type: Activity.Type
  /**
   * Sometimes a particular {@link Activity} is happening to a particular
   * {@link Item}. In those cases, this property will feature a
   * {@link Item.Reference reference} to the associated {@link Item}
   */
  item?: Item.Reference
  /**
   * Optional text appended to the step name in the process tracking UI.
   * Useful for displaying progress information (e.g., "(50/1000)")
   */
  extra?: string
}
export namespace Activity {
  /**
   * Represents some sort of {@link Activity} that can be executed in the system
   */
  export enum Type {
    /**
     * Getting the content of an object from S3
     */
    RetrievingS3Object = 'get-s3-obj',
    /**
     * Parsing an Excel workbook such that it can be interfaced with
     * programmatically
     */
    ParsingExcelWorkbook = 'parse-excel',
    /**
     * Writing records into the DMS
     */
    PuttingDmsRecords = 'put-dms-records',
    /**
     * Parsing an Excel worksheet and extracting & preprocessing DMS records
     * from it
     */
    ExtractingDmsRecordsFromExcelWorksheet = 'extract-dms-records-excel',
    /**
     * Refreshing a materialized view for gl records
     */
    RateRefreshGlRecords = 'rate-refresh-gl',
    /**
     * Writing gl records to the dms, after materialized view has been refreshed
     */
    RateWriteGlRecordsToDms = 'rate-write-gl-to-dms',
    /**
     *  Calculates rate pools (basic building block of rate reports)
     */
    RateCalculatePools = 'rate-calc-pools',
    /**
     * Allocates rate pools to rate reports
     */
    RateCalculateAllocations = 'rate-calc-allocs',
    /**
     * Generates rate reports content
     */
    RateGenerateReportContent = 'rate-gen-report',
    /**
     * Creating a new scenario in dynamoDB
     */
    ScenarioAddToList = 'add-scenario-to-list',
    /**
     * Setting up the scenario infrastructure
     */
    ScenarioSettingUp = 'setting-up-scenario',
    /**
     * Running various scenario importers to import data into the scenario
     */
    ScenarioImporting = 'importing-scenario-data',
    /**
     * Assigning a user to a scenario
     *
     * TODO: this should probably be abstracted to cover all assignments, but
     * that would require  some solution for filtering based on the type of
     * assignment.
     */
    ScenarioAssigningUser = 'assign-user-to-scenario',
    /**
     * Gathering the data needed to generate a file's content
     */
    GeneratingFileContent = 'generating-file-content',
    /**
     * Rendering generated content into a downloadable file
     */
    RenderingFile = 'rendering-file',
    /**
     * Storing a generated file so it can be downloaded
     */
    StoringFile = 'storing-file',
  }
  // Needs to be before namespace is defined
  const TYPES: ReadonlySet<Type> = new Set(Object.values(Type) as Type[])
  export namespace Type {
    export const ALL: readonly Type[] = Array.from(TYPES)
    export const isType = TypeChecker.Factory.oneOf(TYPES)
    export const assertIsType: TypeAsserter<Type> =
      TypeAsserter.Factory.from(isType)
  }

  /**
   * Represents the {@link Execution} of an {@link Activity}
   *
   * __NOTE:__ An {@link Activity} is never not started, since we only register
   * activities when they start
   */
  // NOTE: execution.Execution is already Readonly since you don't want to
  // change the details of an execution. So we might as well do the same for
  // Activity.Execution
  export type Execution = Readonly<
    Activity & Required.Semi<execution.Execution, 'status'>
  >
  export namespace Execution {
    /**
     * Identifies an {@link Activity.Execution}
     */
    export type Id = string
    export namespace Id {
      /**
       * Generates a random {@link Activity.Execution.Id}
       */
      export function generate(): Id {
        return Word.generate()
      }

      /**
       * Derives a deterministic {@link Id} from the given {@link Activity},
       * ensuring that activities with the same type and item reference
       * always produce the same ID. This enables upsert semantics —
       * repeated calls to {@link createActivity} for the same activity
       * update the existing entry rather than creating duplicates
       */
      export function from(activity: Readonly<Activity>): Id {
        return activity.item
          ? `${activity.type}::${activity.item.type}:${activity.item.id}`
          : activity.type
      }
    }
  }
}
